import { ApolloError } from '@apollo/client';
import { AxiosResponse } from 'axios';
import { GraphQLError } from 'graphql';

export type StatusError = 400 | 401 | 403 | 404 | 409 | 500;

export type CustomError = {
  code: string;
  status: StatusError;
  message: string;
  metadata: any;
};

export const getErrors = (err: ApolloError): CustomError[] => {
  let graphqlErrors: readonly GraphQLError[] = [];
  const networkError = err.networkError as unknown as AxiosResponse | null;

  if (err.graphQLErrors && err.graphQLErrors.length > 0) {
    graphqlErrors = err.graphQLErrors;
  } else if (networkError && networkError.data && networkError.data.errors) {
    graphqlErrors = networkError.data.errors;
  }

  const errors: CustomError[] = [];
  graphqlErrors.forEach(({ message, extensions }) => {
    if (!extensions) {
      return;
    }

    errors.push({
      code: extensions.code,
      status: extensions.status,
      message,
      metadata: extensions.metadata,
    });
  });

  return errors;
};

export type ErrorQuery = {
  status?: StatusError;
  code?: string;
};

export const queryCustomError = (errors: CustomError[], query: ErrorQuery) =>
  errors.find(
    (x) =>
      (typeof query.status !== 'undefined' ? x.status === query.status : true) &&
      (query.code ? x.code === query.code : true),
  );

export const queryApolloError = (err: ApolloError, query: ErrorQuery) => queryCustomError(getErrors(err), query);

export const hasCustomError = (errors: CustomError[], status: StatusError, code?: string) =>
  errors.find((x) => x.status === status && (code ? x.code === code : true));

export const hasApolloError = (err: ApolloError, status: StatusError, code?: string) =>
  hasCustomError(getErrors(err), status, code);

export const getErrorByCode = (err, code: string): CustomError | undefined =>
  getErrors(err).find((e) => e.code === code);

export const makeGraphQLError = (status: number, code: string, message: string, metadata?: any) => {
  return new ApolloError({
    graphQLErrors: [
      {
        message,
        extensions: {
          code,
          status,
          metadata,
        },
        locations: [],
        source: undefined,
        path: [],
        positions: [],
        originalError: new Error(message),
        nodes: [],
        name: code,
      },
    ] as readonly GraphQLError[],
  });
};
